import React, { useEffect, useState } from 'react';

import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
  Toolbar,
  Typography,
} from '@material-ui/core';

import BackIcon from '@material-ui/icons/ArrowBack';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import { ApplicationState } from '../store';
import RegistrationFlow from '../components/registrationFlow/RegistrationFlow';
import { MessageSnackbar } from '../components/MessageSnackbar';

import { FieldValueState } from '../components/registrationFlow';
import { stackActions } from '../store/stack/actions';
import { Prompt } from 'react-router';
import { RoutePaths } from '../Routes';

import { history } from '../helpers/history';

const useStyles = makeStyles((theme) => ({
  rootBox: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  appBar: {
    padding: 0,
  },
  appBarOffset: theme.mixins.toolbar,
  loading: {
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backButton: {
    marginRight: 20,
    cursor: 'pointer',
  },
  title: {
    flexGrow: 1,
  },
}));

const Registration = () => {
  const { t } = useTranslation(['translations']);
  const dispatch = useDispatch();

  const [isConfirmCancelOpen, setIsConfirmCancelOpen] = React.useState(false);
  const [autofillFields, setAutofillFields] = React.useState<FieldValueState[]>(
    [],
  );
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  const classes = useStyles();

  const departmentId = useSelector(
    (state: ApplicationState) => state.user.selectedDepartment?.id,
  );

  const measurementCard = useSelector((state: ApplicationState) => {
    const department = state.user.selectedDepartment;

    if (!department) {
      return undefined;
    }

    return state.stacks.departments[department.id]?.measurementCard;
  });

  const isLoading = !measurementCard;

  const backButtonClicked = () => {
    setIsConfirmCancelOpen(true);
  };

  const cancelCloseClicked = () => {
    setIsConfirmCancelOpen(false);
  };

  const confirmCloseClicked = () => {
    setConfirmedNavigation(true);
  };

  const handleFlowCompleted = () => {
    setConfirmedNavigation(true);
  };

  function handleAutofillChanged(autoFillFields: FieldValueState[]) {
    setAutofillFields(autoFillFields);
  }

  function autofillClicked() {
    if (departmentId) {
      for (const field of autofillFields) {
        dispatch(stackActions.updateFieldValue(departmentId, field));
      }
    }
  }

  const handleBlockedNavigation = (): boolean => {
    // Based on this guide:
    // https://michaelchan-13570.medium.com/using-react-router-v4-prompt-with-custom-modal-component-ca839f5faf39
    if (!confirmedNavigation) {
      setIsConfirmCancelOpen(true);
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (confirmedNavigation) {
      history.replace(RoutePaths.Overview);
    }
  }, [confirmedNavigation]);

  return (
    <Box className={classes.rootBox}>
      <Prompt when={true} message={handleBlockedNavigation} />
      <AppBar className={classes.appBar}>
        <Toolbar>
          <BackIcon
            onClick={backButtonClicked}
            className={classes.backButton}
          />

          <Typography variant="h6" className={classes.title}>
            {t('registration_page.title')}
          </Typography>
          {autofillFields.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={autofillClicked}
            >
              {t('registration_page.autofill')}
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <div className={classes.appBarOffset} />
      {isLoading && (
        <Box className={classes.loading}>
          <CircularProgress color="secondary" />
        </Box>
      )}
      {!isLoading && measurementCard && (
        <RegistrationFlow
          measurementCard={measurementCard}
          onFlowCompleted={handleFlowCompleted}
          onAutofillChanged={handleAutofillChanged}
        ></RegistrationFlow>
      )}
      <Dialog
        open={isConfirmCancelOpen}
        onClose={cancelCloseClicked}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('registration_page.confirm_cancel_title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('registration_page.confirm_cancel_text')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelCloseClicked} color="primary">
            {t('common.no')}
          </Button>
          <Button onClick={confirmCloseClicked} color="primary">
            {t('common.yes')}
          </Button>
        </DialogActions>
      </Dialog>
      <MessageSnackbar />
    </Box>
  );
};

export default Registration;
